"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readQuantity } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "refrigerator-size-calculator",
  "kitchen-space-planner",
  "refrigerator-coil-cleaning-savings-calculator",
  "energy-cost-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Refrigerator Water Line Pressure Calculator",
  category: "Installation & Setup" as const,
  mediumDescription:
    "Estimate the water pressure delivered to your fridge ice maker after accounting for line length, line diameter, and inline filter drop. Sized against manufacturer-published 20–120 PSI ice-maker requirements with 40–80 PSI as the optimal range.",
  faqs: [
    { question: "What water pressure does a fridge ice maker need?", answer: "Most refrigerator manufacturers (Whirlpool, GE, LG, Samsung) specify 20–120 PSI inlet pressure, with 40–80 PSI as the optimal performance range. Below 20 PSI, ice production slows dramatically." },
    { question: "Why does line length matter?", answer: "Friction inside the supply tubing causes pressure drop. A 1/4\" line loses roughly 1.5 PSI per 100 feet at typical residential flow rates. Longer runs and smaller diameters compound the loss." },
    { question: "Should I use 1/4\" or 3/8\" line?", answer: "1/4\" copper or braided line is standard for short runs (<25 ft) without filters. 3/8\" line drops pressure roughly 3× less per foot — better for long runs or homes with inline filters." },
    { question: "How much pressure does an inline filter add?", answer: "A typical refrigerator-rated carbon-block filter adds approximately 5 PSI of pressure drop when new. As the filter clogs, the drop increases — replacing the filter every 6 months is the manufacturer recommendation." },
    { question: "What if my house pressure is over 80 PSI?", answer: "The Plumbing Code (UPC/IPC) requires a pressure-reducing valve when static pressure exceeds 80 PSI. High pressure damages ice-maker inlet valves and can cause flooding from worn fittings." },
    { question: "Can I use plastic tubing for the fridge water line?", answer: "Most manufacturers recommend copper or braided stainless steel. Some allow PEX or polyethylene rated for potable water, but plastic is more prone to kinks and chemical leaching. Copper or braided stainless is the safe default." },
  ],
  relatedTools: ["refrigerator-size-calculator", "kitchen-space-planner", "refrigerator-coil-cleaning-savings-calculator"],
}

export function WaterLinePressureCalculatorPageClient() {
  const [housePressure, setHousePressure] = useState("60")
  const [lineLength, setLineLength] = useState("25")
  const [lineDiameter, setLineDiameter] = useState("0.25")
  const [hasFilter, setHasFilter] = useState("no")

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    // A supply with no static pressure delivers no water, and there is always a
    // run of line between the shutoff and the fridge — so both floor at real
    // minimums. The diameter bounds span the three options in the select.
    const pressure = readQuantity(housePressure, { min: 1, max: 200 })
    const length = readQuantity(lineLength, { min: 1, max: 500 })
    const diameter = readQuantity(lineDiameter, { min: 0.25, max: 0.5 })
    if (pressure === null || length === null || diameter === null) return null

    let pressureDropPer100Ft = 0
    if (diameter === 0.25) pressureDropPer100Ft = 1.5
    else if (diameter === 0.375) pressureDropPer100Ft = 0.5
    else pressureDropPer100Ft = 0.2

    let pressureDrop = (length / 100) * pressureDropPer100Ft
    if (hasFilter === "yes") pressureDrop += 5

    const pressureAtFridge = pressure - pressureDrop
    const flowRate = pressureAtFridge * diameter * diameter * 0.5

    let status: "excellent" | "good" | "marginal" | "insufficient" = "good"
    let recommendation = ""
    if (pressureAtFridge < 20) {
      status = "insufficient"
      recommendation = "Below 20 PSI — ice maker won't function reliably. Increase line diameter or boost pressure."
    } else if (pressureAtFridge < 30) {
      status = "marginal"
      recommendation = "Marginal — ice production will be slow. Upgrade to 3/8\" line or shorten the run."
    } else if (pressureAtFridge > 120) {
      status = "marginal"
      recommendation = "Above 120 PSI — install a pressure-reducing valve to protect the ice-maker valve (code requires PRV >80 PSI)."
    } else if (pressureAtFridge >= 40 && pressureAtFridge <= 80) {
      status = "excellent"
      recommendation = "Optimal — within manufacturer-recommended 40–80 PSI range."
    } else {
      status = "good"
      recommendation = "Acceptable — within 20–120 PSI ice-maker spec."
    }

    return {
      pressureAtFridge: parseFloat(pressureAtFridge.toFixed(1)),
      pressureDrop: parseFloat(pressureDrop.toFixed(1)),
      flowRate: parseFloat(flowRate.toFixed(2)),
      status,
      recommendation,
    }
  }, [housePressure, lineLength, lineDiameter, hasFilter])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="Will your ice maker get"
      heroTitleItalic="enough"
      heroTitlePost="pressure?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~30 sec", "Manufacturer 40–80 PSI spec"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "40–80", label: "PSI optimal", note: "Whirlpool / GE / LG" },
            { value: "20–120", label: "PSI range", note: "Min–max spec" },
            { value: "1.5", label: "PSI drop / 100 ft", note: "1/4\" line" },
            { value: "+5", label: "PSI drop", note: "Inline filter" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "refrigerator-water-line-pressure-calculator")}
      cta={{
        title: "Plumbing a fridge with ice maker? Shop French door.",
        body: "Browse French door refrigerators at Fridge.com — the most popular configuration for ice + water dispensers, with through-the-door dispensing on most models.",
        buttonLabel: "Browse French door fridges",
        buttonHref: "/french-door-refrigerators",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Four inputs.
                <br />
                Real fridge PSI.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Pressure at fridge = house PSI − line drop − filter drop.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    House pressure
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={200}
                      step="1"
                      value={housePressure}
                      onChange={(e) => setHousePressure(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">PSI</span>
                  </div>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Most US homes: 40–80 PSI. Code requires PRV above 80 PSI.
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Line length
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={500}
                      step="1"
                      value={lineLength}
                      onChange={(e) => setLineLength(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">ft</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Line diameter
                  </label>
                  <select
                    value={lineDiameter}
                    onChange={(e) => setLineDiameter(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="0.25">1/4&quot; (standard)</option>
                    <option value="0.375">3/8&quot; (better flow)</option>
                    <option value="0.5">1/2&quot; (best flow)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Inline filter
                  </label>
                  <select
                    value={hasFilter}
                    onChange={(e) => setHasFilter(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="no">No external filter</option>
                    <option value="yes">Yes (+5 PSI drop)</option>
                  </select>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Pressure at fridge
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(36px, 8vw, 88px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.pressureAtFridge ?? "—"}
                    <span className="text-[20px] md:text-[24px] font-black tracking-tighter text-white/60 ml-2">PSI</span>
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.recommendation ?? "Enter your line specs."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Pressure drop</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.pressureDrop ?? "—"} PSI</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Status</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight capitalize">{result?.status ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pick the right fridge"
          title="French door fridges to compare"
          description="A through-the-door dispenser needs the line pressure you just checked."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                <em className="italic text-[#ff9951]">Manufacturer</em> spec. Plumbing code.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Pressure ranges (20–120 PSI spec, 40–80 optimal) come from Whirlpool, GE, LG, and Samsung installation manuals. Pressure-drop coefficients reflect typical residential supply-line friction at ice-maker flow rates.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Line drop by diameter", d: "1/4\": ~1.5 PSI per 100 ft. 3/8\": ~0.5. 1/2\": ~0.2. Smaller diameter = exponentially more friction." },
                { n: "02", t: "Filter drop", d: "Inline carbon-block filter adds ~5 PSI drop when new; more as it clogs. Replace every 6 months per most manufacturer guidance." },
                { n: "03", t: "Status bands", d: "<20 PSI: insufficient. 20–40: marginal/acceptable. 40–80: optimal. 80–120: high but acceptable. >120: install PRV." },
                { n: "04", t: "Code reminder", d: "UPC and IPC require a pressure-reducing valve when static pressure exceeds 80 PSI to protect fixtures and the ice-maker inlet valve." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Side-by-side fridges with dispensers"
          titleMatch={["dispenser"]}
          description="Eye-level water and ice dispensing — same plumbing requirements as French door, different ergonomics."
          collectionType="side-by-side"
          fallbackCollectionType="bottom-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
